import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { getUserFacingError } from '../../services/errorMessage';

const ResetPassword = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('This password setup link is invalid. Please request a new one.');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword: formData.newPassword,
      });

      navigate('/login', {
        replace: true,
        state: { message: response.data.message },
      });
    } catch (err) {
      setError(getUserFacingError(err, 'Unable to set your password'));
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      backgroundColor: theme.colors.authPageBg,
    },
    card: {
      width: '100%',
      maxWidth: '460px',
      borderRadius: '8px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
    },
    body: {
      padding: '24px',
    },
    title: {
      fontSize: '26px',
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
      marginBottom: '24px',
      lineHeight: '1.6',
    },
    error: {
      padding: '12px',
      borderRadius: '6px',
      backgroundColor: theme.colors.error + '20',
      color: theme.colors.error,
      fontSize: '14px',
      marginBottom: '16px',
    },
  };

  return (
    <div style={styles.container}>
      <Card cardStyle={styles.card} bodyStyle={styles.body}>
        <h1 style={styles.title}>Set Your Password</h1>
        <p style={styles.subtitle}>
          Create a secure password to activate your account and continue to your dashboard.
        </p>

        <form onSubmit={handleSubmit}>
          {error && <div style={styles.error}>{error}</div>}

          <Input
            type="password"
            name="newPassword"
            label="New Password"
            value={formData.newPassword}
            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
            placeholder="Create your password"
            required
            helperText="Use at least 6 characters with uppercase, lowercase, and a number."
          />

          <Input
            type="password"
            name="confirmPassword"
            label="Confirm Password"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            placeholder="Confirm your password"
            required
          />

          <Button type="submit" fullWidth disabled={loading}>
            {loading ? 'Saving password...' : 'Set Password'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default ResetPassword;